import { SxProps, Theme } from '@mui/material';

import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { zIndexes } from '@/styles/zIndexes';

import { Dialog } from '../Dialog';

/**
 * Full-screen trowser shell. Trowsers are URL-driven: the host derives open
 * state from `useTrowser(key)` (see `TrowserProvider`), so they deep-link,
 * survive reload, and close on browser Back. Page shells that use this only for
 * layout pass {@link TrowserKey.Standalone}.
 */
export type FullScreenModalProps = React.PropsWithChildren<{
  /**
   * The trowser's unique key. Optional while trowsers migrate to URL-driven
   * state one at a time; will become required.
   */
  trowserKey?: TrowserKey;
  onClose?: () => void;
  isOpen?: boolean;
  dialogSx?: SxProps<Theme>;
  backdropSx?: SxProps<Theme>;
  /**
   * When true, disables MUI's `enforceFocus` polling that forces focus back
   * inside the modal. Useful when the modal contains contenteditable elements
   * that conflict with the focus trap (e.g., slide title editors).
   */
  disableEnforceFocus?: boolean;
}>;

export function FullScreenModal({
  trowserKey,
  isOpen,
  onClose,
  children,
  backdropSx = {},
  dialogSx = {},
  disableEnforceFocus,
}: FullScreenModalProps) {
  return (
    <Dialog
      fullScreen
      open={!!isOpen}
      // Surfaces the trowser key in the DOM for debugging / e2e targeting.
      data-trowser-key={trowserKey}
      // Fullscreen covers the viewport, so backdrop clicks shouldn't happen;
      // disabling them defensively prevents accidental closes from stale click
      // events when nested modals unmount and MUI restores focus.
      disableBackdropClick
      disableEnforceFocus={disableEnforceFocus}
      PaperProps={{ sx: backdropSx }}
      sx={{ zIndex: zIndexes.MODAL, ...dialogSx }}
      onClose={onClose}
    >
      {children}
    </Dialog>
  );
}
